/**
 * Generates a branded printable PDF for a single order.
 * Used from admin (AdminCommandes) and client (MesCommandes) views.
 */
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { ORDER_STATUS_LABELS } from "@/lib/orderStatus";

const orderRef = (id) => "#" + (id || "").slice(0, 8).toUpperCase();

const formatDateCH = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("fr-CH", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
};

const formatDateOnlyCH = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("fr-CH");
    } catch {
        return iso;
    }
};

export function generateOrderPdf(order, options = {}) {
    if (!order || !order.id) throw new Error("Commande invalide");

    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const left = 40;
    const right = pageWidth - 40;

    // --- Header ---------------------------------------------------------
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.setTextColor(212, 188, 124);
    doc.text("BOUCHERIE COSTA", left, 50);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(120, 120, 120);
    doc.text("Espace Pro · Yverdon-les-Bains", left, 66);
    doc.text("info@boucherie-costa.ch", left, 78);

    // Right side: order ref + status
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(212, 188, 124);
    doc.text(`Bon de commande ${orderRef(order.id)}`, right, 50, { align: "right" });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(60, 60, 60);
    doc.text(`Statut : ${ORDER_STATUS_LABELS[order.status] || order.status || "—"}`, right, 66, { align: "right" });
    doc.text(`Passée le ${formatDateCH(order.created_at)}`, right, 78, { align: "right" });

    // Gold hairline
    doc.setDrawColor(212, 188, 124);
    doc.setLineWidth(0.6);
    doc.line(left, 92, right, 92);

    // --- Client block ---------------------------------------------------
    let y = 118;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.text("CLIENT", left, y);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    y += 16;
    const raison = order.user_raison_sociale || "";
    if (raison) {
        doc.text(raison, left, y);
        y += 14;
    }
    const contact = `${order.user_prenom || ""} ${order.user_nom || ""}`.trim();
    if (contact) {
        doc.text(contact, left, y);
        y += 14;
    }
    if (order.user_email) {
        doc.setTextColor(80, 80, 80);
        doc.text(order.user_email, left, y);
        y += 12;
    }
    if (order.user_telephone) {
        doc.text(order.user_telephone, left, y);
        y += 12;
    }
    if (order.user_ide_tva) {
        doc.text(`IDE/TVA : ${order.user_ide_tva}`, left, y);
        y += 12;
    }
    doc.setTextColor(0, 0, 0);

    // Delivery info block (right side)
    let ry = 118;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("LIVRAISON", right, ry, { align: "right" });
    doc.setFont("helvetica", "normal");
    ry += 16;
    doc.text(`Date souhaitée : ${formatDateOnlyCH(order.delivery_date)}`, right, ry, { align: "right" });
    ry += 14;

    y = Math.max(y, ry) + 10;

    // --- Items table ----------------------------------------------------
    const body = (order.items || []).map((it) => {
        const qty = Number(it.quantity ?? it.quantite ?? 0);
        const unit = it.unite || "kg";
        const price = it.prix_kg_ht == null ? null : Number(it.prix_kg_ht);
        const priceStr = price == null ? "Sur demande" : `CHF ${price.toFixed(2)} / ${unit}`;
        const lineTotal = price == null ? "—" : `CHF ${(price * qty).toFixed(2)}`;
        return [
            it.code || "—",
            it.article || "",
            it.provenance || "—",
            `${qty} ${unit}`,
            priceStr,
            lineTotal,
        ];
    });

    autoTable(doc, {
        startY: y + 4,
        head: [["Code", "Article", "Provenance", "Quantité", "Prix HT", "Total HT"]],
        body,
        styles: { font: "helvetica", fontSize: 9, textColor: 40, cellPadding: 5 },
        headStyles: { fillColor: [0, 0, 0], textColor: [212, 188, 124], fontStyle: "bold" },
        alternateRowStyles: { fillColor: [245, 240, 230] },
        columnStyles: {
            0: { cellWidth: 60 },
            3: { halign: "right", cellWidth: 60 },
            4: { halign: "right", cellWidth: 90 },
            5: { halign: "right", cellWidth: 80, textColor: [140, 100, 40], fontStyle: "bold" },
        },
        margin: { left, right: 40 },
    });

    let afterY = doc.lastAutoTable.finalY + 16;

    // Ensure room; add page if needed
    const pageHeight = doc.internal.pageSize.getHeight();
    if (afterY > pageHeight - 130) {
        doc.addPage();
        afterY = 50;
    }

    // --- Totals ---------------------------------------------------------
    doc.setDrawColor(212, 188, 124);
    doc.setLineWidth(0.8);
    doc.line(right - 220, afterY, right, afterY);
    afterY += 18;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    doc.text("Total HT", right - 220, afterY);
    doc.setTextColor(140, 100, 40);
    doc.text(`CHF ${Number(order.total_ht || 0).toFixed(2)}`, right, afterY, { align: "right" });
    afterY += 22;

    // --- Notes ----------------------------------------------------------
    if (order.notes && String(order.notes).trim()) {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        doc.setTextColor(0, 0, 0);
        doc.text("NOTES", left, afterY);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.setTextColor(60, 60, 60);
        const wrapped = doc.splitTextToSize(String(order.notes), right - left);
        doc.text(wrapped, left, afterY + 14);
        afterY += 14 + wrapped.length * 12 + 8;
    }

    // --- Review (if present) --------------------------------------------
    if (order.review) {
        if (afterY > pageHeight - 100) {
            doc.addPage();
            afterY = 50;
        }
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        doc.setTextColor(0, 0, 0);
        doc.text("ÉVALUATION CLIENT", left, afterY);
        afterY += 14;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.setTextColor(60, 60, 60);
        const rating = Number(order.review.rating || 0);
        doc.text(`Note : ${rating}/5`, left, afterY);
        afterY += 12;
        if (order.review.comment) {
            const w = doc.splitTextToSize(`« ${order.review.comment} »`, right - left);
            doc.text(w, left, afterY);
            afterY += w.length * 12;
        }
        if (order.review.has_complaint && order.review.complaint_text) {
            doc.setTextColor(180, 60, 60);
            doc.text("Réclamation : ", left, afterY);
            afterY += 12;
            const w = doc.splitTextToSize(order.review.complaint_text, right - left);
            doc.text(w, left, afterY);
            afterY += w.length * 12;
        }
    }

    // --- Footer ---------------------------------------------------------
    const footerY = pageHeight - 30;
    doc.setDrawColor(212, 188, 124);
    doc.setLineWidth(0.4);
    doc.line(left, footerY - 12, right, footerY - 12);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(120, 120, 120);
    doc.text("Boucherie Costa · Yverdon-les-Bains · pro.boucherie-costa.ch", left, footerY);
    doc.text(
        `Document généré le ${new Date().toLocaleString("fr-CH")}`,
        right,
        footerY,
        { align: "right" }
    );

    const filename = options.filename || `commande-${orderRef(order.id).replace("#", "")}.pdf`;
    if (options.printMode) {
        // Open in a new tab and trigger the print dialog automatically
        const blobUrl = doc.output("bloburl");
        const w = window.open(blobUrl, "_blank");
        if (w) {
            // give the browser a beat to render the PDF viewer
            setTimeout(() => {
                try { w.focus(); w.print(); } catch { /* silent */ }
            }, 400);
        }
        return blobUrl;
    }
    doc.save(filename);
    return filename;
}
